import { useState, type FormEvent } from 'react';
import SiteHeader from '@/components/feature/SiteHeader';
import SiteFooter from '@/components/feature/SiteFooter';
import ScrollToTopButton from '@/components/feature/ScrollToTopButton';
import { useSeo } from '@/hooks/useSeo';

const FORM_SUBMIT_ADDR = 'https://readdy.ai/api/form/db6cnhi2sgkaik1undo0';

const TEAM = [
  { name: '内容运营组', icon: 'ri-layout-grid-line', desc: '负责影视资料的整理、分类与每日更新维护。' },
  { name: '产品体验组', icon: 'ri-cursor-line', desc: '打磨搜索、筛选与浏览体验，让找片更快更准。' },
  { name: '技术支持组', icon: 'ri-code-s-slash-line', desc: '保障站点稳定运行与页面加载性能。' },
];

const VALUES = [
  { title: '海量内容', desc: '覆盖电影、电视剧、综艺三大板块，多种题材与地区。', icon: 'ri-film-line' },
  { title: '精准检索', desc: '关键词模糊搜索，支持按类型、年代快速筛选。', icon: 'ri-search-eye-line' },
  { title: '清爽体验', desc: '现代门户风格，信息层次清晰，浏览舒适不杂乱。', icon: 'ri-layout-masonry-line' },
];

export default function About() {
  useSeo(
    '关于我们 - 91大神影视大全',
    '了解91大神影视大全的品牌定位、内容范围与团队，并可通过反馈表单与联系方式与我们取得联系。',
  );

  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get('website_alt') ?? '').trim();
    if (honeypot) {
      // 蜜罐命中：不发送任何请求，给出与正常提交一致的通用反馈
      setStatus('success');
      setFormError('');
      form.reset();
      return;
    }

    formData.delete('website_alt');

    setStatus('submitting');
    setFormError('');

    try {
      const response = await fetch(FORM_SUBMIT_ADDR, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });

      const responseText = await response.text();
      let parsed: { code?: string; message?: string; meta?: { message?: string; detail?: string } } | null =
        null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
      const isSpam = typeof serverMsg === 'string' && serverMsg.toLowerCase().includes('spam');

      if (response.ok && parsed?.code === 'OK' && !isSpam) {
        setStatus('success');
        form.reset();
      } else {
        setStatus('error');
        setFormError(
          serverMsg && typeof serverMsg === 'string' && serverMsg.length < 200
            ? serverMsg
            : '提交失败，请稍后重试或直接通过邮箱联系我们。',
        );
      }
    } catch {
      setStatus('error');
      setFormError('网络异常，提交失败，请稍后重试或通过邮箱联系我们。');
    }
  };

  return (
    <div className="min-h-screen w-full bg-background-100 flex flex-col">
      <SiteHeader />

      <main className="w-full flex-1">
        <section className="w-full bg-primary-900 text-background-50">
          <div className="w-full px-4 md:px-8 py-12 md:py-16">
            <h1 className="font-heading font-extrabold text-3xl md:text-4xl">关于 91大神</h1>
            <p className="mt-4 max-w-2xl text-sm md:text-base text-primary-100/85 leading-relaxed">
              91大神影视大全致力于为广泛影视爱好者提供最新、丰富、多样的影视资源，包括电影、电视剧和综艺栏目，覆盖多种类型与题材，帮助用户快速查找感兴趣的内容。
            </p>
          </div>
        </section>

        <section className="w-full px-4 md:px-8 py-10 md:py-14">
          <h2 className="font-heading font-bold text-2xl text-foreground-950">我们的使命</h2>
          <p className="mt-4 max-w-3xl text-sm md:text-base text-foreground-600 leading-relaxed">
            我们希望成为影迷的“影视资料库与导航”。无论你是想追一部正在热播的剧集，还是想找一部适合周末放松的电影，91大神都希望用清晰的结构与舒适的浏览体验，让你用最少的时间找到想看的内容。站点聚焦内容展示、搜索与筛选，倡导理性、便捷的影视浏览方式。
          </p>

          <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-4 md:gap-6">
            {VALUES.map((value) => (
              <div
                key={value.title}
                className="rounded-lg bg-background-50 border border-background-200 p-5"
              >
                <span className="w-12 h-12 rounded-lg bg-accent-50 text-accent-600 flex items-center justify-center">
                  <i className={`${value.icon} text-2xl`} />
                </span>
                <h3 className="mt-4 font-heading font-semibold text-base text-foreground-950">
                  {value.title}
                </h3>
                <p className="mt-2 text-sm text-foreground-600 leading-relaxed">{value.desc}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="w-full px-4 md:px-8 py-10 md:py-14 bg-background-50">
          <h2 className="font-heading font-bold text-2xl text-foreground-950">运营团队</h2>
          <p className="mt-3 text-sm text-foreground-600 max-w-3xl">
            以下为示例团队介绍，用于展示站点的组织结构。
          </p>
          <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-4 md:gap-6">
            {TEAM.map((member) => (
              <div
                key={member.name}
                className="rounded-lg bg-background-100 border border-background-200 p-5 flex items-start gap-4"
              >
                <span className="w-11 h-11 rounded-full bg-primary-500 text-background-50 flex items-center justify-center shrink-0">
                  <i className={`${member.icon} text-xl`} />
                </span>
                <div>
                  <h3 className="font-heading font-semibold text-base text-foreground-950">
                    {member.name}
                  </h3>
                  <p className="mt-1.5 text-sm text-foreground-600 leading-relaxed">
                    {member.desc}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </section>

        <section className="w-full px-4 md:px-8 py-10 md:py-14">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">
            <div>
              <h2 className="font-heading font-bold text-2xl text-foreground-950">
                反馈与建议
              </h2>
              <p className="mt-3 text-sm text-foreground-600 leading-relaxed">
                我们非常重视你的意见。无论是内容补充、体验优化还是功能建议，都欢迎通过下方表单告诉我们，也可以直接通过联系方式与我们取得联系。
              </p>

              <div className="mt-6 space-y-4">
                <a
                  href="mailto:support@91dashen.example.com"
                  className="flex items-center gap-3 rounded-lg bg-background-50 border border-background-200 p-4 hover:border-primary-300 transition-colors cursor-pointer"
                >
                  <span className="w-10 h-10 rounded-lg bg-primary-50 text-primary-600 flex items-center justify-center">
                    <i className="ri-mail-line text-lg" />
                  </span>
                  <span>
                    <span className="block text-sm font-medium text-foreground-950">邮箱</span>
                    <span className="block text-sm text-foreground-600">
                      support@91dashen.example.com
                    </span>
                  </span>
                </a>
                <a
                  href="https://weibo.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-3 rounded-lg bg-background-50 border border-background-200 p-4 hover:border-primary-300 transition-colors cursor-pointer"
                >
                  <span className="w-10 h-10 rounded-lg bg-primary-50 text-primary-600 flex items-center justify-center">
                    <i className="ri-weibo-line text-lg" />
                  </span>
                  <span>
                    <span className="block text-sm font-medium text-foreground-950">微博</span>
                    <span className="block text-sm text-foreground-600">@91大神影视</span>
                  </span>
                </a>
                <div className="flex items-center gap-3 rounded-lg bg-background-50 border border-background-200 p-4">
                  <span className="w-10 h-10 rounded-lg bg-primary-50 text-primary-600 flex items-center justify-center">
                    <i className="ri-wechat-line text-lg" />
                  </span>
                  <span>
                    <span className="block text-sm font-medium text-foreground-950">微信公众号</span>
                    <span className="block text-sm text-foreground-600">91大神影视</span>
                  </span>
                </div>
              </div>
            </div>

            <div className="rounded-lg bg-background-50 border border-background-200 p-5 md:p-6">
              {status === 'success' ? (
                <div className="py-10 flex flex-col items-center text-center">
                  <span className="w-14 h-14 rounded-full bg-primary-50 text-primary-600 flex items-center justify-center">
                    <i className="ri-checkbox-circle-line text-3xl" />
                  </span>
                  <h3 className="mt-4 font-heading font-semibold text-lg text-foreground-950">
                    感谢你的反馈
                  </h3>
                  <p className="mt-2 text-sm text-foreground-600">
                    我们已经收到你的建议，会尽快查看处理。
                  </p>
                  <button
                    type="button"
                    onClick={() => setStatus('idle')}
                    className="mt-5 px-6 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    再填一条
                  </button>
                </div>
              ) : (
                <form
                  id="feedback-form"
                  data-readdy-form
                  onSubmit={handleSubmit}
                  className="space-y-4"
                >
                  <div>
                    <label
                      htmlFor="fb-name"
                      className="block text-sm font-medium text-foreground-800 mb-1.5"
                    >
                      你的称呼
                    </label>
                    <input
                      id="fb-name"
                      name="name"
                      type="text"
                      required
                      placeholder="请输入称呼"
                      className="w-full h-11 rounded-md bg-background-100 border border-background-200 focus:border-primary-400 outline-none px-3 text-sm text-foreground-900 placeholder:text-foreground-400"
                    />
                  </div>

                  <div>
                    <label
                      htmlFor="fb-email"
                      className="block text-sm font-medium text-foreground-800 mb-1.5"
                    >
                      邮箱
                    </label>
                    <input
                      id="fb-email"
                      name="email"
                      type="email"
                      required
                      placeholder="用于回复你的邮箱"
                      className="w-full h-11 rounded-md bg-background-100 border border-background-200 focus:border-primary-400 outline-none px-3 text-sm text-foreground-900 placeholder:text-foreground-400"
                    />
                  </div>

                  <div>
                    <label
                      htmlFor="fb-type"
                      className="block text-sm font-medium text-foreground-800 mb-1.5"
                    >
                      反馈类型
                    </label>
                    <select
                      id="fb-type"
                      name="feedback_type"
                      className="w-full h-11 rounded-md bg-background-100 border border-background-200 focus:border-primary-400 outline-none px-3 text-sm text-foreground-900"
                    >
                      <option value="内容补充">内容补充</option>
                      <option value="体验建议">体验建议</option>
                      <option value="功能需求">功能需求</option>
                      <option value="问题反馈">问题反馈</option>
                    </select>
                  </div>

                  <div>
                    <label
                      htmlFor="fb-message"
                      className="block text-sm font-medium text-foreground-800 mb-1.5"
                    >
                      反馈内容
                    </label>
                    <textarea
                      id="fb-message"
                      name="message"
                      required
                      maxLength={500}
                      rows={5}
                      placeholder="请描述你的建议或问题（最多500字）"
                      className="w-full rounded-md bg-background-100 border border-background-200 focus:border-primary-400 outline-none px-3 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 resize-none"
                    />
                  </div>

                  <div className="form-hp-field" aria-hidden="true">
                    <label htmlFor="fb-website-alt">请勿填写此项</label>
                    <input
                      id="fb-website-alt"
                      name="website_alt"
                      type="text"
                      tabIndex={-1}
                      autoComplete="off"
                      readOnly
                    />
                  </div>

                  {status === 'error' && formError && (
                    <p className="text-sm text-accent-700 bg-accent-50 border border-accent-200 rounded-md px-3 py-2">
                      {formError}
                    </p>
                  )}

                  <button
                    type="submit"
                    disabled={status === 'submitting'}
                    className="w-full h-11 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {status === 'submitting' ? '提交中...' : '提交反馈'}
                  </button>
                  <p className="text-xs text-foreground-500 text-center">
                    你的反馈将用于站点改进，我们不会用于其他用途。
                  </p>
                </form>
              )}
            </div>
          </div>
        </section>
      </main>

      <SiteFooter />
      <ScrollToTopButton />
    </div>
  );
}